import React, { useEffect, useRef, useState } from "react";
import { generateOtp, guestEmailOtp, verifyOtp } from "../configs/WebService";
import userStore from "../zustand/userStore";
const OTP_LENGTH = 6;
const EmailVerifyPopup = ({
  isOpen,
  onClose,
  defaultEmail = "",
  id,
  onVerified,
}) => {
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState(defaultEmail || "");
  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(""));
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const inputRefs = useRef([]);

  const setUser = userStore.getState().setUser;

  useEffect(() => {
    if (isOpen) {
      setStep("email");
      setEmail(defaultEmail || "");
      setOtp(Array(OTP_LENGTH).fill(""));
    }
  }, [isOpen, defaultEmail]);

  if (!isOpen) return null;

  const sendOtpToEmail = async (toEmail) => {
    setSending(true);
    try {
      const response = await fetch(guestEmailOtp, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id, to: toEmail, otpType: "email_verify" }),
      });
      const data = await response.json();
      if (response.ok) {
        alert("OTP sent to your email.");
        setStep("otp");
        setTimeout(() => inputRefs.current[0]?.focus(), 50);
      } else {
        alert(
          "Failed to send OTP: " +
            (data?.result_message || "Please try again."),
        );
      }
    } catch (err) {
      console.error("Network error while sending OTP:", err);
      alert("Network error. Please try again later.");
    } finally {
      setSending(false);
    }
  };

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) {
      alert("Please enter an email.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      alert("Please enter a valid email.");
      return;
    }
    sendOtpToEmail(trimmed);
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const next = [...otp];
    next[index] = value;
    setOtp(next);
    if (value && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    const text = (e.clipboardData.getData("text") || "").replace(/\D/g, "");
    if (!text) return;
    e.preventDefault();
    const next = Array(OTP_LENGTH).fill("");
    for (let i = 0; i < OTP_LENGTH && i < text.length; i++) {
      next[i] = text[i];
    }
    setOtp(next);
    const focusIndex = Math.min(text.length, OTP_LENGTH) - 1;
    inputRefs.current[focusIndex]?.focus();
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    const code = otp.join("");
    if (code.length !== OTP_LENGTH) {
      alert("Please enter the full OTP.");
      return;
    }
    setVerifying(true);
    try {
      const response = await fetch(verifyOtp, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          to: email.trim(),
          otp: code,
          otpType: "email_verify",
        }),
      });
      const data = await response.json();
      if (response.ok) {
        const userData = JSON.parse(localStorage.getItem("userData"));
        if (userData) {
          userData.is_email = 1;
          localStorage.setItem("userData", JSON.stringify(userData));
          setUser({
            ...userData,
            is_email: 1,
          });
        }

        alert("Email verified successfully!");
        // if (typeof onVerified === "function") {
        //   onVerified({ email: email.trim(), data });
        // }
        onClose?.();
      } else {
        alert(
          "Verification failed: " +
            (data?.message || data?.error || "Invalid OTP"),
        );
      }
    } catch (err) {
      console.error("OTP verification error:", err);
      alert("Network error. Please try again.");
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div style={styles.overlay} role="dialog" aria-modal="true">
      <div style={styles.modal}>
        <div style={styles.header}>
          <h3 style={styles.title}>
            {step === "email" ? "Verify Your Email" : "Enter OTP"}
          </h3>
          <span style={styles.close} onClick={onClose}>
            &times;
          </span>
        </div>

        {step === "email" ? (
          <form onSubmit={handleEmailSubmit} style={styles.form}>
            <p style={styles.message}>
              Enter your email to receive a verification OTP.
            </p>
            <input
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={styles.input}
              autoFocus
              required
            />
            <button
              type="submit"
              disabled={sending}
              style={{
                ...styles.submitBtn,
                ...(sending ? styles.disabledBtn : {}),
              }}
            >
              {sending ? "Sending..." : "Send OTP"}
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerify} style={styles.form}>
            <p style={styles.message}>
              We&apos;ve sent a 6-digit code to <strong>{email}</strong>. Enter
              it below.
            </p>
            <div style={styles.otpRow}>
              {otp.map((digit, idx) => (
                <input
                  key={idx}
                  ref={(el) => (inputRefs.current[idx] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(idx, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(idx, e)}
                  onPaste={idx === 0 ? handleOtpPaste : undefined}
                  style={styles.otpInput}
                />
              ))}
            </div>
            <button
              type="submit"
              disabled={verifying}
              style={{
                ...styles.submitBtn,
                ...(verifying ? styles.disabledBtn : {}),
              }}
            >
              {verifying ? "Verifying..." : "Verify"}
            </button>
            {/* <button
              type="button"
              onClick={() => setStep("email")}
              style={styles.linkBtn}
            >
              Change email
            </button> */}
          </form>
        )}
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(15, 23, 42, 0.55)",
    zIndex: 9999,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "20px",
  },
  modal: {
    background: "#ffffff",
    borderRadius: "16px",
    width: "100%",
    maxWidth: "440px",
    boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    overflow: "hidden",
  },
  header: {
    padding: "18px 22px",
    borderBottom: "1px solid #e2e8f0",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  title: {
    margin: 0,
    fontSize: "18px",
    fontWeight: 700,
    color: "#1e293b",
  },
  close: {
    fontSize: "24px",
    cursor: "pointer",
    color: "#94a3b8",
    lineHeight: 1,
  },
  form: {
    padding: "22px",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  message: {
    margin: 0,
    fontSize: "14px",
    color: "#475569",
    lineHeight: 1.6,
  },
  input: {
    width: "100%",
    padding: "12px 14px",
    border: "1.5px solid #cbd5e1",
    borderRadius: "10px",
    fontSize: "15px",
    outline: "none",
    boxSizing: "border-box",
  },
  otpRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: "8px",
  },
  otpInput: {
    width: "44px",
    height: "52px",
    textAlign: "center",
    fontSize: "20px",
    fontWeight: 600,
    border: "1.5px solid #cbd5e1",
    borderRadius: "10px",
    outline: "none",
    background: "#f8fafc",
    color: "#1e293b",
  },
  submitBtn: {
    padding: "12px 18px",
    border: "none",
    borderRadius: "10px",
    background: "linear-gradient(135deg, #0065ff, #5a4fcf)",
    color: "#fff",
    fontSize: "15px",
    fontWeight: 600,
    cursor: "pointer",
  },
  disabledBtn: {
    opacity: 0.7,
    cursor: "not-allowed",
  },
  linkBtn: {
    background: "transparent",
    border: "none",
    color: "#0065ff",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer",
    alignSelf: "center",
    padding: 0,
  },
};

export default EmailVerifyPopup;
